import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, User, Save, Edit3, Target, Home, DollarSign, TrendingUp, Shield, Wrench, Clock, MapPin, Sparkles } from 'lucide-react';
import { RendPlexLogo } from '../components/RendPlexLogo';
import DarkModeToggle from '../components/DarkModeToggle';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Footer } from '../components/LegalDisclaimers';
import UserMenu from '../components/UserMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STRATEGY_KEYS = ['cash_flow', 'appreciation', 'brrrr', 'long_term_hold', 'portfolio_growth', 'first_investment'];
const PROPERTY_TYPE_KEYS = ['duplex', 'triplex', 'fourplex', 'five_plus', 'apartment_building', 'mixed_use', 'other'];
const EXPERIENCE_KEYS = ['beginner', 'intermediate', 'experienced'];
const RISK_KEYS = ['conservative', 'moderate', 'aggressive'];
const RENOVATION_KEYS = ['turnkey', 'light', 'major', 'brrrr'];
const HORIZON_KEYS = ['short', 'three_to_five', 'five_to_ten', 'ten_plus'];

const emptyProfile = {
  strategies: [],
  experience: null,
  property_types: [],
  target_locations: [],
  target_price_min: '',
  target_price_max: '',
  down_payment_type: 'percent',
  down_payment_amount: '',
  down_payment_percent: '',
  target_monthly_cash_flow: '',
  min_cap_rate: '',
  min_cash_on_cash: '',
  risk_tolerance: null,
  renovation_preference: null,
  time_horizon: null,
};

export default function InvestorProfile() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [uResp, pResp] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/investor-profile`, { withCredentials: true }),
        ]);
        setUser(uResp.data);
        const p = pResp.data || {};
        setProfile({
          ...emptyProfile,
          ...p,
          target_price_min: p.target_price_min ?? '',
          target_price_max: p.target_price_max ?? '',
          down_payment_amount: p.down_payment_amount ?? '',
          down_payment_percent: p.down_payment_percent ?? '',
          target_monthly_cash_flow: p.target_monthly_cash_flow ?? '',
          min_cap_rate: p.min_cap_rate ?? '',
          min_cash_on_cash: p.min_cash_on_cash ?? '',
          down_payment_type: p.down_payment_type ?? 'percent',
          strategies: p.strategies || [],
          property_types: p.property_types || [],
          target_locations: p.target_locations || [],
        });
        // Auto-enter edit mode for first-time users
        if (!p.is_complete) setEditMode(true);
      } catch (e) {
        if (e.response?.status === 401) navigate('/');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const toggleInArray = (field, key) => {
    setProfile((prev) => {
      const arr = prev[field] || [];
      return {
        ...prev,
        [field]: arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key],
      };
    });
  };

  const setField = (field, value) => {
    setProfile((prev) => {
      const next = { ...prev, [field]: value };
      // Live-validate fields the moment a user changes them so inline
      // errors show up without requiring a Save click.
      setErrors((prevErrs) => {
        const errs = { ...prevErrs, [field]: null };
        // Numeric sanity for the field being edited.
        const numFields = ['target_price_min', 'target_price_max', 'target_monthly_cash_flow', 'min_cap_rate', 'min_cash_on_cash', 'down_payment_amount'];
        if (numFields.includes(field) && value !== '' && value !== null) {
          const n = Number(value);
          if (isNaN(n) || n < 0) errs[field] = t('investorProfile.validation.priceInvalid');
        }
        // Price range coherency (check both directions).
        const pMin = field === 'target_price_min' ? value : next.target_price_min;
        const pMax = field === 'target_price_max' ? value : next.target_price_max;
        if (pMin !== '' && pMax !== '' && Number(pMin) > Number(pMax)) {
          errs.target_price_max = t('investorProfile.validation.priceRangeInvalid');
        } else if (errs.target_price_max === t('investorProfile.validation.priceRangeInvalid')) {
          errs.target_price_max = null;
        }
        // Down-payment percent bounds.
        if (field === 'down_payment_percent' && value !== '' && value !== null) {
          const n = Number(value);
          if (isNaN(n) || n < 0 || n > 100) errs.down_payment_percent = t('investorProfile.validation.percentInvalid');
        }
        return errs;
      });
      return next;
    });
  };

  const validate = () => {
    const errs = {};
    const numFields = ['target_price_min', 'target_price_max', 'target_monthly_cash_flow', 'min_cap_rate', 'min_cash_on_cash'];
    numFields.forEach((f) => {
      const v = profile[f];
      if (v !== '' && v !== null && (isNaN(Number(v)) || Number(v) < 0)) {
        errs[f] = t('investorProfile.validation.priceInvalid');
      }
    });
    if (profile.target_price_min && profile.target_price_max) {
      if (Number(profile.target_price_min) > Number(profile.target_price_max)) {
        errs.target_price_max = t('investorProfile.validation.priceRangeInvalid');
      }
    }
    if (profile.down_payment_type === 'percent' && profile.down_payment_percent !== '') {
      const v = Number(profile.down_payment_percent);
      if (isNaN(v) || v < 0 || v > 100) errs.down_payment_percent = t('investorProfile.validation.percentInvalid');
    }
    if (profile.down_payment_type === 'amount' && profile.down_payment_amount !== '') {
      const v = Number(profile.down_payment_amount);
      if (isNaN(v) || v < 0) errs.down_payment_amount = t('investorProfile.validation.priceInvalid');
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error(t('errors.saveFailed'));
      return;
    }
    setSaving(true);
    try {
      const toNumOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
      const payload = {
        strategies: profile.strategies,
        experience: profile.experience,
        property_types: profile.property_types,
        target_locations: profile.target_locations,
        target_price_min: toNumOrNull(profile.target_price_min),
        target_price_max: toNumOrNull(profile.target_price_max),
        down_payment_type: profile.down_payment_type,
        down_payment_amount: profile.down_payment_type === 'amount' ? toNumOrNull(profile.down_payment_amount) : null,
        down_payment_percent: profile.down_payment_type === 'percent' ? toNumOrNull(profile.down_payment_percent) : null,
        target_monthly_cash_flow: toNumOrNull(profile.target_monthly_cash_flow),
        min_cap_rate: toNumOrNull(profile.min_cap_rate),
        min_cash_on_cash: toNumOrNull(profile.min_cash_on_cash),
        risk_tolerance: profile.risk_tolerance,
        renovation_preference: profile.renovation_preference,
        time_horizon: profile.time_horizon,
      };
      const resp = await axios.put(`${BACKEND_URL}/api/investor-profile`, payload, { withCredentials: true });
      const p = resp.data;
      setProfile({
        ...emptyProfile,
        ...p,
        target_price_min: p.target_price_min ?? '',
        target_price_max: p.target_price_max ?? '',
        down_payment_amount: p.down_payment_amount ?? '',
        down_payment_percent: p.down_payment_percent ?? '',
        target_monthly_cash_flow: p.target_monthly_cash_flow ?? '',
        min_cap_rate: p.min_cap_rate ?? '',
        min_cash_on_cash: p.min_cash_on_cash ?? '',
        down_payment_type: p.down_payment_type ?? 'percent',
        strategies: p.strategies || [],
        property_types: p.property_types || [],
        target_locations: p.target_locations || [],
      });
      toast.success(t('investorProfile.saved'));
      setEditMode(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('investorProfile.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#45C04A]" />
      </div>
    );
  }

  const hasProfile = profile.is_complete;

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] text-foreground transition-colors duration-300">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-[#0B0E14]/70 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex justify-between items-center">
          <Link to="/dashboard" className="flex items-center gap-2"><RendPlexLogo size="md" /></Link>
          <div className="flex items-center gap-2">
            <DarkModeToggle />
            <LanguageSwitcher variant="minimal" />
            <Button data-testid="back-to-dashboard-button" onClick={() => navigate('/dashboard')} variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> {t('nav.backToDashboard')}
            </Button>
            <UserMenu user={user} />
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#45C04A] to-[#65D95A] flex items-center justify-center flex-shrink-0">
              <User className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 data-testid="investor-profile-title" className="font-primary font-extrabold text-3xl lg:text-4xl text-[#12355B] dark:text-white tracking-tight">
                {t('investorProfile.pageTitle')}
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
                {t('investorProfile.pageSubtitle')}
              </p>
            </div>
          </div>
          {!editMode && hasProfile && (
            <Button data-testid="edit-profile-button" onClick={() => setEditMode(true)}
              className="bg-[#12355B] hover:bg-[#1565C0] dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl">
              <Edit3 className="w-4 h-4 mr-2" /> {t('investorProfile.editProfile')}
            </Button>
          )}
        </div>

        {/* Summary card when not editing and complete */}
        {!editMode && hasProfile && (
          <ProfileSummary profile={profile} t={t} />
        )}

        {/* Edit mode */}
        {editMode && (
          <div className="space-y-6">
            {/* Strategy */}
            <Section icon={Target} title={t('investorProfile.sections.strategy')} hint={t('investorProfile.sections.strategyHint')}>
              <PillGrid
                testIdPrefix="strategy"
                keys={STRATEGY_KEYS}
                translate={(k) => t(`investorProfile.strategies.${k}`)}
                selected={profile.strategies}
                onToggle={(k) => toggleInArray('strategies', k)}
                multi
              />
            </Section>

            {/* Experience */}
            <Section icon={TrendingUp} title={t('investorProfile.sections.experience')}>
              <PillGrid
                testIdPrefix="experience"
                keys={EXPERIENCE_KEYS}
                translate={(k) => t(`investorProfile.experience.${k}`)}
                selected={profile.experience ? [profile.experience] : []}
                onToggle={(k) => setField('experience', profile.experience === k ? null : k)}
              />
            </Section>

            {/* Property types */}
            <Section icon={Home} title={t('investorProfile.sections.propertyTypes')} hint={t('investorProfile.sections.propertyTypesHint')}>
              <PillGrid
                testIdPrefix="ptype"
                keys={PROPERTY_TYPE_KEYS}
                translate={(k) => t(`investorProfile.propertyTypes.${k}`)}
                selected={profile.property_types}
                onToggle={(k) => toggleInArray('property_types', k)}
                multi
              />
            </Section>

            {/* Target locations */}
            <Section icon={MapPin} title={t('investorProfile.sections.location')} hint={t('investorProfile.sections.locationHint')}>
              <textarea
                data-testid="target-locations-input"
                rows={3}
                value={(profile.target_locations || []).join('\n')}
                onChange={(e) => setField('target_locations', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 dark:text-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#45C04A]/30"
                placeholder="Montréal&#10;Longueuil&#10;Laval"
              />
            </Section>

            {/* Price range */}
            <Section icon={DollarSign} title={t('investorProfile.sections.priceRange')}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <NumberField label={t('investorProfile.fields.min')} testId="target-price-min-input"
                  prefix="$" value={profile.target_price_min}
                  onChange={(v) => setField('target_price_min', v)} error={errors.target_price_min} />
                <NumberField label={t('investorProfile.fields.max')} testId="target-price-max-input"
                  prefix="$" value={profile.target_price_max}
                  onChange={(v) => setField('target_price_max', v)} error={errors.target_price_max} />
              </div>
            </Section>

            {/* Down payment */}
            <Section icon={DollarSign} title={t('investorProfile.sections.downPayment')}>
              <div className="flex gap-2 mb-3">
                <TogglePill testId="dp-percent" active={profile.down_payment_type === 'percent'}
                  onClick={() => setField('down_payment_type', 'percent')}>
                  {t('investorProfile.fields.byPercent')}
                </TogglePill>
                <TogglePill testId="dp-amount" active={profile.down_payment_type === 'amount'}
                  onClick={() => setField('down_payment_type', 'amount')}>
                  {t('investorProfile.fields.byAmount')}
                </TogglePill>
              </div>
              {profile.down_payment_type === 'percent' ? (
                <NumberField label={t('investorProfile.fields.percent')} testId="down-payment-percent-input"
                  suffix="%" value={profile.down_payment_percent}
                  onChange={(v) => setField('down_payment_percent', v)} error={errors.down_payment_percent} />
              ) : (
                <NumberField label={t('investorProfile.fields.amount')} testId="down-payment-amount-input"
                  prefix="$" value={profile.down_payment_amount}
                  onChange={(v) => setField('down_payment_amount', v)} error={errors.down_payment_amount} />
              )}
            </Section>

            {/* Target Monthly Cash Flow, Cap, CoC */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Section icon={DollarSign} title={t('investorProfile.sections.targetCashFlow')}>
                <NumberField testId="target-monthly-cf-input" prefix="$" suffix="/mo"
                  value={profile.target_monthly_cash_flow} placeholder={t('investorProfile.fields.monthlyPlaceholder')}
                  onChange={(v) => setField('target_monthly_cash_flow', v)} error={errors.target_monthly_cash_flow} />
              </Section>
              <Section icon={TrendingUp} title={t('investorProfile.sections.minCapRate')}>
                <NumberField testId="min-cap-rate-input" suffix="%"
                  value={profile.min_cap_rate} placeholder={t('investorProfile.fields.capPlaceholder')}
                  onChange={(v) => setField('min_cap_rate', v)} error={errors.min_cap_rate} />
              </Section>
              <Section icon={TrendingUp} title={t('investorProfile.sections.minCoc')}>
                <NumberField testId="min-coc-input" suffix="%"
                  value={profile.min_cash_on_cash} placeholder={t('investorProfile.fields.cocPlaceholder')}
                  onChange={(v) => setField('min_cash_on_cash', v)} error={errors.min_cash_on_cash} />
              </Section>
            </div>

            {/* Risk / Renovation / Horizon */}
            <Section icon={Shield} title={t('investorProfile.sections.risk')}>
              <PillGrid
                testIdPrefix="risk"
                keys={RISK_KEYS}
                translate={(k) => t(`investorProfile.risk.${k}`)}
                selected={profile.risk_tolerance ? [profile.risk_tolerance] : []}
                onToggle={(k) => setField('risk_tolerance', profile.risk_tolerance === k ? null : k)}
              />
            </Section>

            <Section icon={Wrench} title={t('investorProfile.sections.renovation')}>
              <PillGrid
                testIdPrefix="reno"
                keys={RENOVATION_KEYS}
                translate={(k) => t(`investorProfile.renovation.${k}`)}
                selected={profile.renovation_preference ? [profile.renovation_preference] : []}
                onToggle={(k) => setField('renovation_preference', profile.renovation_preference === k ? null : k)}
              />
            </Section>

            <Section icon={Clock} title={t('investorProfile.sections.horizon')}>
              <PillGrid
                testIdPrefix="horizon"
                keys={HORIZON_KEYS}
                translate={(k) => t(`investorProfile.horizon.${k}`)}
                selected={profile.time_horizon ? [profile.time_horizon] : []}
                onToggle={(k) => setField('time_horizon', profile.time_horizon === k ? null : k)}
              />
            </Section>

            {/* Save/Cancel */}
            <div className="flex items-center justify-end gap-3 pt-2">
              {hasProfile && (
                <Button data-testid="cancel-edit-button" variant="outline" onClick={() => { setEditMode(false); setErrors({}); }}>
                  {t('common.cancel')}
                </Button>
              )}
              <Button data-testid="save-profile-button" onClick={handleSave} disabled={saving}
                className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] font-semibold rounded-xl px-6">
                <Save className="w-4 h-4 mr-2" />
                {saving ? t('investorProfile.saving') : t('investorProfile.saveProfile')}
              </Button>
            </div>
          </div>
        )}

        {/* Empty state (profile not complete) */}
        {!editMode && !hasProfile && (
          <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-10 text-center">
            <Sparkles className="w-12 h-12 mx-auto text-[#45C04A] mb-4" />
            <p className="text-slate-600 dark:text-slate-300 mb-6 max-w-md mx-auto">{t('investorProfile.summaryEmpty')}</p>
            <Button data-testid="create-profile-cta" onClick={() => setEditMode(true)}
              className="bg-[#12355B] hover:bg-[#1565C0] dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl">
              {t('investorProfile.createCta')}
            </Button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

/* ============= Sub-components ============= */

function Section({ icon: Icon, title, hint, children }) {
  return (
    <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-xl bg-[#12355B]/5 dark:bg-[#45C04A]/10 flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#12355B] dark:text-[#45C04A]" />
        </div>
        <div>
          <h3 className="font-primary font-bold text-[#12355B] dark:text-white text-base">{title}</h3>
          {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function PillGrid({ keys, translate, selected, onToggle, testIdPrefix, multi }) {
  return (
    <div className="flex flex-wrap gap-2">
      {keys.map((k) => {
        const active = selected.includes(k);
        return (
          <button
            key={k}
            type="button"
            data-testid={`${testIdPrefix}-pill-${k}`}
            aria-pressed={active}
            onClick={() => onToggle(k)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
              active
                ? 'bg-[#45C04A] text-[#12355B] border-[#45C04A] shadow-sm'
                : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-[#45C04A]/60'
            }`}
          >
            {translate(k)}
            {multi && active && <span className="ml-1.5">✓</span>}
          </button>
        );
      })}
    </div>
  );
}

function TogglePill({ children, active, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
        active
          ? 'bg-[#12355B] text-white border-[#12355B] dark:bg-[#45C04A] dark:text-[#12355B] dark:border-[#45C04A]'
          : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-slate-300'
      }`}
    >
      {children}
    </button>
  );
}

function NumberField({ label, value, onChange, error, prefix, suffix, testId, placeholder }) {
  return (
    <div>
      {label && <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">{label}</label>}
      <div className="relative flex items-center">
        {prefix && <span className="absolute left-3 text-sm text-slate-400 pointer-events-none">{prefix}</span>}
        <input
          data-testid={testId}
          type="number"
          inputMode="decimal"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          aria-invalid={!!error}
          className={`w-full rounded-xl border ${error ? 'border-red-400' : 'border-slate-200 dark:border-slate-700'} bg-white dark:bg-slate-800 dark:text-white px-4 py-2.5 ${prefix ? 'pl-7' : ''} ${suffix ? 'pr-10' : ''} focus:outline-none focus:ring-2 focus:ring-[#45C04A]/30`}
        />
        {suffix && <span className="absolute right-3 text-sm text-slate-400 pointer-events-none">{suffix}</span>}
      </div>
      {error && (
        <p
          data-testid={testId ? `${testId}-error` : undefined}
          role="alert"
          className="error field-error text-xs text-red-500 mt-1"
        >
          {error}
        </p>
      )}
    </div>
  );
}

function ProfileSummary({ profile, t }) {
  const strategyLabels = (profile.strategies || []).map((k) => t(`investorProfile.strategies.${k}`)).join(' + ');
  const typeLabels = (profile.property_types || []).map((k) => t(`investorProfile.propertyTypes.${k}`)).join(', ');
  const fmtMoney = (v) => (v ? `$${Number(v).toLocaleString()}` : t('investorProfile.notSet'));
  const budget =
    profile.target_price_min || profile.target_price_max
      ? `${fmtMoney(profile.target_price_min)} – ${fmtMoney(profile.target_price_max)}`
      : t('investorProfile.notSet');

  const rows = [
    { label: t('investorProfile.summaryStrategy'), value: strategyLabels || t('investorProfile.notSet') },
    { label: t('investorProfile.summaryTarget'), value: typeLabels || t('investorProfile.notSet') },
    { label: t('investorProfile.summaryBudget'), value: budget },
    { label: t('investorProfile.summaryMinCashFlow'), value: profile.target_monthly_cash_flow ? `$${Number(profile.target_monthly_cash_flow).toLocaleString()}/mo` : t('investorProfile.notSet') },
    { label: t('investorProfile.summaryMinCapRate'), value: profile.min_cap_rate ? `${profile.min_cap_rate}%` : t('investorProfile.notSet') },
    { label: t('investorProfile.summaryRisk'), value: profile.risk_tolerance ? t(`investorProfile.risk.${profile.risk_tolerance}`) : t('investorProfile.notSet') },
  ];

  return (
    <div data-testid="profile-summary" className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8 shadow-card">
      <h2 className="font-primary font-extrabold text-2xl text-[#12355B] dark:text-white mb-6 tracking-tight">
        {t('investorProfile.summaryTitle')}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {rows.map((r) => (
          <div key={r.label}>
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">{r.label}</p>
            <p className="text-[#12355B] dark:text-white font-medium">{r.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
